import { UM } from '../_components/tokens';
import { EyeToggle, SourceBadge, TrashIcon } from './primitives';
import type { Layer, Step, StepId } from './types';

// ─── Ligne de couche (dans la sélection) ─────────────────────────────────────

function SelectedRow({
  layer,
  visible,
  onToggleVisible,
  onRemove,
}: {
  layer: Layer;
  visible: boolean;
  onToggleVisible: () => void;
  onRemove: () => void;
}) {
  return (
    <div
      title={layer.hint}
      style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 12px 6px 28px', position: 'relative' }}
      onMouseEnter={(e) => (e.currentTarget.style.background = UM.bgHover)}
      onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}
    >
      <EyeToggle visible={visible} onClick={onToggleVisible} />
      <span style={{ flex: 1, fontSize: 13, color: visible ? UM.text : UM.textMuted, fontWeight: visible ? 500 : 400 }}>
        {layer.label}
      </span>
      <SourceBadge source={layer.source} />
      <button
        onClick={onRemove}
        title="Retirer de ma sélection"
        style={{ border: 'none', background: 'none', cursor: 'pointer', padding: 4, display: 'inline-flex', flexShrink: 0 }}
      >
        <TrashIcon />
      </button>
    </div>
  );
}

// ─── En-tête d'étape ─────────────────────────────────────────────────────────

function StepHeader({
  step, open, activeInStep, totalInStep, onToggle,
}: {
  step: Step; open: boolean; activeInStep: number; totalInStep: number; onToggle: () => void;
}) {
  return (
    <button
      onClick={onToggle}
      style={{
        width: '100%', textAlign: 'left', border: 'none', cursor: 'pointer',
        display: 'flex', alignItems: 'center', gap: 12,
        padding: '10px 12px',
        background: open ? UM.accentLight : 'transparent',
        borderLeft: `3px solid ${open ? UM.accent : 'transparent'}`,
      }}
    >
      <span style={{
        width: 24, height: 24, borderRadius: '50%',
        background: UM.bgHeader, color: UM.text,
        border: `1px solid ${UM.border}`,
        fontSize: 12, fontWeight: 700,
        display: 'inline-flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
      }}>
        {step.number}
      </span>
      <span style={{ flex: 1, minWidth: 0 }}>
        <span style={{ display: 'block', fontSize: 13, fontWeight: 600, color: UM.text }}>{step.title}</span>
        <span style={{ display: 'block', fontSize: 11, color: UM.textMuted, marginTop: 1 }}>{step.subtitle}</span>
      </span>
      {totalInStep > 0 && (
        <span style={{
          fontSize: 11, fontWeight: 600, color: UM.accent,
          background: '#fff', padding: '2px 6px', borderRadius: 10,
          border: `1px solid ${UM.accent}`,
        }}>
          {activeInStep}/{totalInStep}
        </span>
      )}
      <span style={{ color: UM.textMuted, fontSize: 10, flexShrink: 0 }}>
        {open ? '▲' : '▼'}
      </span>
    </button>
  );
}

// ─── Panel principal (sélection filtrée par étape) ───────────────────────────

export interface HybridPanelProps {
  steps: Step[];
  libraryByStep: Map<StepId, Layer[]>;
  visible: Set<string>;
  openStep: StepId | null;
  activeCount: number;
  librarySize: number;
  onOpenPicker: () => void;
  onToggleVisible: (id: string) => void;
  onToggleStep: (id: StepId) => void;
  onRemoveFromLibrary: (id: string) => void;
  onClearAll: () => void;
  onClose: () => void;
}

export function HybridPanel(props: HybridPanelProps) {
  return (
    <div style={{
      width: 340, minWidth: 340, background: UM.bg,
      borderRight: `1px solid ${UM.border}`,
      display: 'flex', flexDirection: 'column',
      height: '100%', overflow: 'hidden', zIndex: 15,
    }}>
      {/* Header */}
      <div style={{ padding: '14px 16px 12px', borderBottom: `1px solid ${UM.border}`, background: UM.bgHeader, flexShrink: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
          <span style={{ fontWeight: 700, fontSize: 15, color: UM.text }}>Couches de données</span>
          <button
            onClick={props.onClose}
            style={{ border: 'none', background: 'none', cursor: 'pointer', color: UM.textMuted, fontSize: 18, lineHeight: 1 }}
          >
            ✕
          </button>
        </div>
        <div style={{ fontSize: 11, color: UM.textDim, lineHeight: 1.4, marginBottom: 8 }}>
          Retrouvez vos couches par phase d'étude. Sélection suggérée par défaut, personnalisable à tout moment.
        </div>
        <button
          onClick={props.onOpenPicker}
          style={{
            padding: 0, border: 'none', background: 'none',
            color: UM.accent, cursor: 'pointer',
            display: 'inline-flex', alignItems: 'center', gap: 4,
            fontSize: 12, fontWeight: 600,
          }}
        >
          <span style={{ fontSize: 14, lineHeight: 1 }}>+</span>
          Ajouter des couches
        </button>
      </div>

      {/* Body — étapes en accordéon avec uniquement les couches sélectionnées */}
      <div style={{ flex: 1, overflowY: 'auto' }}>
        {props.steps.map((step) => {
          const layers = props.libraryByStep.get(step.id) ?? [];
          const active = layers.filter((l) => props.visible.has(l.id)).length;
          const open = props.openStep === step.id;
          return (
            <div key={step.id} style={{ borderTop: `1px solid ${UM.border}` }}>
              <StepHeader
                step={step}
                open={open}
                activeInStep={active}
                totalInStep={layers.length}
                onToggle={() => props.onToggleStep(step.id)}
              />
              {open && (
                <div style={{ padding: '4px 0 10px' }}>
                  {layers.length === 0 ? (
                    <div style={{ padding: '8px 28px', fontSize: 12, color: UM.textDim, fontStyle: 'italic' }}>
                      Aucune couche sélectionnée pour cette étape
                    </div>
                  ) : (
                    layers.map((layer) => (
                      <SelectedRow
                        key={layer.id}
                        layer={layer}
                        visible={props.visible.has(layer.id)}
                        onToggleVisible={() => props.onToggleVisible(layer.id)}
                        onRemove={() => props.onRemoveFromLibrary(layer.id)}
                      />
                    ))
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Footer — état des couches actives */}
      <div style={{
        padding: '10px 16px',
        borderTop: `1px solid ${UM.border}`,
        background: UM.bgHeader,
        flexShrink: 0,
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        fontSize: 12,
      }}>
        <span style={{ color: UM.textMuted }}>
          {props.activeCount === 0 ? 'Aucune couche activée' : (
            <><strong style={{ color: UM.accent }}>{props.activeCount}</strong> couche{props.activeCount > 1 ? 's' : ''} activée{props.activeCount > 1 ? 's' : ''}</>
          )}
          {props.librarySize > 0 && (
            <span style={{ color: UM.textDim }}> / {props.librarySize} sélectionnée{props.librarySize > 1 ? 's' : ''}</span>
          )}
        </span>
        {props.activeCount > 0 && (
          <button
            onClick={props.onClearAll}
            style={{ border: 'none', background: 'none', cursor: 'pointer', color: UM.textMuted, fontSize: 12, textDecoration: 'underline' }}
          >
            Tout masquer
          </button>
        )}
      </div>
    </div>
  );
}
